import { useMemo } from "react";
import { pluralizeForPrefix } from "@smartrr/shared/utils/pluralize";
import { HomeTableAccess } from "../store";
import { BulkAction } from "@shopify/polaris/build/ts/src/components/BulkActions";
import { mapPurchaseStatesWithDelay } from "@vendor-app/utils/mapSubscriptionWithDelay";
import { useActiveOrganizationSelector } from "@vendor-app/app/_state/reducers/organizations";
import { SellingPlanGroupStoreAccess } from "@vendor-app/app/_state/zustand/SellingPlansStore";
import { createAndDownloadCsv } from "@vendor-app/utils/createAndDownloadCsv";
import { DateTime } from "luxon";
import { useCancelConfirmationModal } from "../../Modals/Cancel";
import { SubscriptionContractSubscriptionStatus } from "@smartrr/shared/shopifyGraphQL/api";
import { compact } from "lodash";
import { IPurchaseStateCSV } from "@smartrr/shared/entities/PurchaseState";
import { exportPurchaseStatesToCsvRows } from "@vendor-app/utils/exportPurchaseStatesToCsvRows";
import { IDeserializedPaginatedQuery } from "@smartrr/shared/utils/paginatedQuery";
import { HomeTableFilters } from "../store/filters";

export const useExportToCSVAction = (): BulkAction => {
  const selectedResources = HomeTableAccess.useSelectedData();
  const tableActions = HomeTableAccess.useActions();
  const selectedFilters = HomeTableAccess.useSelectedFilters();
  const activeOrg = useActiveOrganizationSelector();
  const sellingPlanGroups = SellingPlanGroupStoreAccess.useGroups();

  return useMemo(() => {
    return {
      content: "Export to CSV",
      onAction() {
        void tableActions.whileLoading(async () => {
          let rows: IPurchaseStateCSV[] = [];

          if (selectedResources.length) {
            rows = await mapPurchaseStatesWithDelay(selectedResources, activeOrg, sellingPlanGroups);
          } else {
            const query: IDeserializedPaginatedQuery = {
              pageNumber: 0,
              /**
               * Set by filters
               */
              pageSize: 0,
            };

            for (const filter of selectedFilters) {
              const chosenFilter = HomeTableFilters.HomeTableFilters[filter.filterType];
              if (chosenFilter) {
                chosenFilter.addToQuery(query, filter.value);
              }
            }

            rows = await exportPurchaseStatesToCsvRows(activeOrg, sellingPlanGroups, undefined, query);
          }

          const formattedDate = DateTime.now().toFormat("yyyy_MM_dd");
          createAndDownloadCsv(rows, `subscriptions_export_${formattedDate}`);
        });
      },
    };
  }, [selectedResources, activeOrg, selectedFilters, sellingPlanGroups]);
};

const useCancelBulkAction = (): BulkAction | null => {
  const selectedResources = HomeTableAccess.useSelectedData();
  const openCancelationModal = useCancelConfirmationModal(state => state.openModal);

  const areAllSelectedSubscriptionsCancellable = !selectedResources.some(
    cps =>
      cps.purchaseStateStatus !== SubscriptionContractSubscriptionStatus.Active &&
      cps.purchaseStateStatus !== SubscriptionContractSubscriptionStatus.Paused
  );

  return useMemo(() => {
    if (!areAllSelectedSubscriptionsCancellable) {
      return null;
    }

    return {
      content: `Cancel ${pluralizeForPrefix(selectedResources.length, "subscription")}`,
      onAction: () => openCancelationModal({}),
      destructive: true,
    };
  }, [selectedResources]);
};

export const useBulkActions = (): BulkAction[] => {
  const exportToCSVAction = useExportToCSVAction();
  const cancelAction = useCancelBulkAction();

  return useMemo(() => {
    return compact([exportToCSVAction, cancelAction]);
  }, [exportToCSVAction, cancelAction]);
};
